<template>
  <div class="product-detail">
    <v-container>
      <!-- Botão de voltar -->
      <div class="d-flex align-center mb-6">
        <v-btn
          icon
          @click="$router.go(-1)"
          class="mr-2"
        >
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <div>
          <span class="text-body-2 text-uppercase text-medium-emphasis">
            {{ breadcrumb }} 
          </span>
          <h1 class="text-h4 font-weight-bold mt-1">{{ product.name }}</h1>
        </div>
      </div>
      
      <!-- Loading -->
      <div v-if="loading" class="text-center py-8">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>
      
      <!-- Conteúdo do produto -->
      <div v-else>
        <v-row>
          <!-- Galeria de imagens -->
          <v-col cols="12" md="6">
            <div class="image-gallery">
              <v-img
                :src="selectedImage || product.image"
                height="400"
                contain
                class="main-image rounded-lg"
              ></v-img>
              
              <div v-if="product.images && product.images.length > 1" class="mt-4">
                <v-slide-group show-arrows>
                  <v-slide-item
                    v-for="(img, index) in product.images"
                    :key="index"
                  >
                    <v-card
                      class="ma-2 thumbnail"
                      height="80"
                      width="80"
                      :class="{ 'thumbnail-active': selectedImage === img }"
                      @click="selectedImage = img"
                    >
                      <v-img
                        :src="img"
                        height="80"
                        width="80"
                        contain
                      ></v-img>
                    </v-card>
                  </v-slide-item>
                </v-slide-group>
              </div>
            </div>
          </v-col>
          
          <!-- Detalhes do produto -->
          <v-col cols="12" md="6">
            <div class="product-info">
              <!-- Status do estoque -->
              <div class="mb-4">
                <v-chip
                  v-if="!product.inStock"
                  color="red"
                  text-color="white"
                  small
                >
                  Esgotado
                </v-chip>
                <v-chip
                  v-else
                  color="green"
                  text-color="white"
                  small
                >
                  Em estoque
                </v-chip>
                
                <v-chip
                  v-if="product.isNewProduct"
                  color="primary"
                  text-color="white"
                  small
                  class="ml-2"
                >
                  Novo
                </v-chip>
              </div>
              
              <!-- Preço -->
              <div class="text-h3 font-weight-bold primary--text mb-4">
                R$ {{ product.price.toFixed(2).replace('.', ',') }}
              </div>
              
              <v-divider class="mb-4"></v-divider>
              
              <!-- Descrição -->
              <div class="mb-6">
                <h3 class="text-subtitle-1 font-weight-bold mb-2">Descrição</h3>
                <p class="text-body-1">{{ product.description }}</p>
              </div>
              
              <!-- Opções -->
              <div v-if="product.options && product.options.length > 0" class="mb-4">
                <h3 class="text-subtitle-1 font-weight-bold mb-2">Opções</h3>
                <v-select
                  v-model="selectedOption"
                  :items="product.options"
                  label="Selecione uma opção"
                  outlined
                  dense
                  return-object
                ></v-select>
              </div>
              
              <!-- Tamanhos -->
              <div v-if="product.sizes && product.sizes.length > 0" class="mb-4">
                <h3 class="text-subtitle-1 font-weight-bold mb-2">Tamanho</h3>
                <v-chip-group
                  v-model="selectedSize"
                  mandatory
                >
                  <v-chip
                    v-for="(size, index) in product.sizes"
                    :key="index"
                    filter
                    outlined
                  >
                    {{ size }}
                  </v-chip>
                </v-chip-group>
              </div>
              
              <!-- Quantidade -->
              <div class="mb-6">
                <h3 class="text-subtitle-1 font-weight-bold mb-2">Quantidade</h3>
                <div class="d-flex align-center">
                  <v-btn
                    icon
                    @click="decrementQuantity"
                    :disabled="quantity <= 1"
                  >
                    <v-icon>mdi-minus</v-icon>
                  </v-btn>
                  <v-text-field
                    v-model="quantity"
                    type="number"
                    min="1"
                    outlined
                    dense
                    hide-details
                    style="width: 70px"
                    class="mx-2"
                  ></v-text-field>
                  <v-btn
                    icon
                    @click="incrementQuantity"
                    :disabled="!product.inStock"
                  >
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </div>
              </div>
              
              <!-- Botões de ação -->
              <div class="d-flex flex-column flex-sm-row gap-3">
                <v-btn
                  color="primary"
                  x-large
                  block
                  :disabled="!product.inStock"
                  @click="addToCart"
                >
                  <v-icon left>mdi-cart</v-icon>
                  Adicionar ao carrinho
                </v-btn>
                
                <v-btn
                  color="primary"
                  x-large
                  outlined
                  block
                  class="mt-3 mt-sm-0"
                  @click="buyNow"
                  :disabled="!product.inStock"
                >
                  <v-icon left>mdi-flash</v-icon>
                  Comprar agora
                </v-btn>
              </div>
              
              <!-- Detalhes adicionais -->
              <v-expansion-panels
                v-if="product.additionalInfo"
                flat
                class="mt-6"
              >
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <h3 class="text-subtitle-1 font-weight-bold">Detalhes adicionais</h3>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div v-html="product.additionalInfo"></div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
          </v-col>
        </v-row>
        
        <!-- Produtos relacionados -->
        <div v-if="relatedProducts.length > 0" class="mt-12">
          <h2 class="text-h5 font-weight-bold mb-4">Produtos relacionados</h2>
          <product-carousel :products="relatedProducts" @product-click="navigateToProduct" />
        </div>
      </div>
    </v-container>
  </div>
</template>

<script>
import ProductCarousel from '@/components/shop/products/ProductCarousel.vue';

export default {
  name: 'ProductDetail',
  components: {
    ProductCarousel
  },
  data() {
    return {
      product: {},
      relatedProducts: [],
      loading: true,
      selectedImage: null,
      selectedOption: null,
      selectedSize: null,
      quantity: 1,
      breadcrumb: ''
    };
  },
  async created() {
    try {
      await this.fetchProductData();
      await this.fetchRelatedProducts();
      this.setBreadcrumb();
    } catch (error) {
      console.error('Erro ao carregar dados do produto:', error);
      this.$toast.error('Não foi possível carregar os detalhes do produto.');
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async fetchProductData() {
      const productId = this.$route.params.productId;
      const orgSlug = this.$route.params.orgSlug;
      
      // Simulando delay de rede
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Mockup de dados para exemplo
      this.product = {
        id: productId,
        name: this.getProductName(productId),
        price: Math.floor(Math.random() * 200) + 50,
        image: `https://placehold.co/600x600`,
        images: [
          `https://placehold.co/600x600`,
          `https://placehold.co/600x600`,
          `https://placehold.co/600x600`,
          `https://placehold.co/600x600`
        ],
        description: this.getProductDescription(productId),
        inStock: Math.random() > 0.2,
        isNewProduct: Math.random() > 0.7,
        sizes: this.getProductSizes(productId),
        colors: this.getProductColors(productId),
        options: this.getProductOptions(productId),
        additionalInfo: this.getAdditionalInfo(productId),
        category: this.getRandomCategory(),
        rating: (Math.random() * 2 + 3).toFixed(1), // Rating entre 3.0 e 5.0
        reviewCount: Math.floor(Math.random() * 200) + 5
      };
      
      // Seleciona a primeira imagem como padrão
      this.selectedImage = this.product.images ? this.product.images[0] : this.product.image;
    },
    
    getProductName(id) {
      const productNames = [
        'Camiseta Premium Exclusiva',
        'Boné Edição Limitada',
        'Caneca Personalizada',
        'Adesivo Colecionável',
        'Pôster Autografado',
        'Ecobag Especial',
        'Moletom Official Fan',
        'Chaveiro Comemorativo',
        'Bandana Temática',
        'Almofada Decorativa'
      ];
      
      return `${productNames[id % productNames.length]} #${id}`;
    },
    
    getProductDescription(id) {
      const descriptions = [
        'Produto exclusivo e de alta qualidade, fabricado com os melhores materiais do mercado. Design moderno e confortável, perfeito para qualquer ocasião.',
        'Item colecionável para os verdadeiros fãs. Edição limitada com acabamento premium e detalhes exclusivos que não encontrará em nenhum outro lugar.',
        'Feito à mão por artesãos especializados, cada peça é única e traz um pouco da nossa essência para o seu dia a dia. Durabilidade garantida!',
        'Desenvolvido pensando no conforto e estilo, este produto se adapta perfeitamente ao seu estilo de vida, seja para eventos casuais ou formais.',
        'Produto sustentável, produzido com materiais ecológicos e processos que respeitam o meio ambiente. Qualidade e responsabilidade em um só item.'
      ];
      
      return descriptions[id % descriptions.length];
    },
    
    getProductSizes(id) {
      // 70% de chance de ter tamanhos
      if (Math.random() > 0.3) {
        const sizeSets = [
          ['P', 'M', 'G', 'GG'],
          ['PP', 'P', 'M', 'G', 'GG', 'XGG'],
          ['34', '36', '38', '40', '42', '44'],
          ['Único'],
          ['S', 'M', 'L', 'XL', 'XXL']
        ];
        return sizeSets[id % sizeSets.length];
      }
      return null;
    },
    
    getProductColors(id) {
      // 60% de chance de ter cores
      if (Math.random() > 0.4) {
        const colorSets = [
          ['Preto', 'Branco', 'Vermelho', 'Azul'],
          ['Verde', 'Amarelo', 'Roxo', 'Rosa'],
          ['Cinza', 'Marrom', 'Bege', 'Laranja'],
          ['Azul Marinho', 'Verde Musgo', 'Vinho', 'Off White']
        ];
        return colorSets[id % colorSets.length];
      }
      return null;
    },
    
    getProductOptions(id) {
      // 50% de chance de ter opções
      if (Math.random() > 0.5) {
        const optionSets = [
          [
            { text: 'Padrão', value: 'padrao' },
            { text: 'Edição Especial', value: 'especial' },
            { text: 'Edição Limitada', value: 'limitada' }
          ],
          [
            { text: 'Modelo Básico', value: 'basico' },
            { text: 'Modelo Premium', value: 'premium' }
          ],
          [
            { text: 'Sem Personalização', value: 'sem_personalizacao' },
            { text: 'Com Nome', value: 'com_nome' },
            { text: 'Com Número', value: 'com_numero' },
            { text: 'Nome e Número', value: 'nome_numero' }
          ]
        ];
        return optionSets[id % optionSets.length];
      }
      return null;
    },
    
    getAdditionalInfo(id) {
      const infoBlocks = [
        `<p>Informações adicionais sobre o produto:</p>
        <ul>
          <li><strong>Material:</strong> 100% Algodão Premium</li>
          <li><strong>Garantia:</strong> 12 meses contra defeitos</li>
          <li><strong>Origem:</strong> Nacional</li>
          <li><strong>Instruções de Lavagem:</strong> Lavar a mão ou máquina no ciclo delicado</li>
        </ul>`,
        
        `<p>Especificações técnicas:</p>
        <ul>
          <li><strong>Composição:</strong> 80% Algodão, 20% Poliéster</li>
          <li><strong>Estampagem:</strong> Digital de alta definição</li>
          <li><strong>Durabilidade:</strong> Alta resistência a lavagens</li>
          <li><strong>Certificações:</strong> Produto certificado ABNT</li>
        </ul>`,
        
        `<p>Informações de envio:</p>
        <ul>
          <li><strong>Peso:</strong> 300g</li>
          <li><strong>Dimensões da embalagem:</strong> 30cm x 20cm x 5cm</li>
          <li><strong>Prazo de fabricação:</strong> 2 dias úteis</li>
          <li><strong>Tempo de entrega:</strong> 3-7 dias úteis</li>
        </ul>`
      ];
      
      return infoBlocks[id % infoBlocks.length];
    },
    
    getRandomCategory() {
      const categories = [
        'Vestuário', 'Acessórios', 'Decoração', 'Colecionáveis', 'Papelaria'
      ];
      return categories[Math.floor(Math.random() * categories.length)];
    },
    
    async fetchRelatedProducts() {
      // Simulando delay de rede
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Mockup de dados para exemplo
      this.relatedProducts = this.generateMockProducts(8);
    },
    
    generateMockProducts(count) {
      const products = [];
      const categories = ['Vestuário', 'Acessórios', 'Decoração', 'Colecionáveis'];
      const names = [
        'Camiseta Fan', 'Boné Premium', 'Caneca Especial', 'Chaveiro Colecionável',
        'Pulseira Temática', 'Adesivo Limited', 'Ecobag Personalizada', 'Mousepad',
        'Pin Comemorativo', 'Bandana Estampada', 'Moletom Exclusivo', 'Squeeze Térmico'
      ];

      for (let i = 1; i <= count; i++) {
        const id = Math.floor(Math.random() * 1000) + 1;
        const nameIndex = Math.floor(Math.random() * names.length);
        products.push({
          id: `related-${id}`,
          name: names[nameIndex],
          price: Math.floor(Math.random() * 120) + 30,
          image: `https://placehold.co/300x300`,
          description: `Produto da mesma categoria do item principal. Combina perfeitamente e completa sua coleção.`,
          inStock: Math.random() > 0.3,
          category: categories[Math.floor(Math.random() * categories.length)],
          isNewProduct: Math.random() > 0.7
        });
      }
      return products;
    },
    
    setBreadcrumb() {
      // Poderia buscar o nome da coleção se o produto veio de uma
      this.breadcrumb = `Loja / ${this.$route.params.orgSlug} / Produtos`;
    },
    
    incrementQuantity() {
      if (this.product.inStock) {
        this.quantity++;
      }
    },
    
    decrementQuantity() {
      if (this.quantity > 1) {
        this.quantity--;
      }
    },
    
    addToCart() {
      if (!this.product.inStock) return;
      
      const cartItem = {
        productId: this.product.id,
        name: this.product.name,
        price: this.product.price,
        quantity: this.quantity,
        option: this.selectedOption,
        size: this.selectedSize !== null ? this.product.sizes[this.selectedSize] : null,
        image: this.product.image
      };
      
      // Aqui você implementaria a lógica para adicionar ao carrinho
      // Por exemplo:
      // this.$store.dispatch('cart/addItem', cartItem);
      
      console.log('Adicionado ao carrinho:', cartItem);
      this.$toast.success('Produto adicionado ao carrinho!');
    },
    
    buyNow() {
      this.addToCart();
      // Redirecionar para a página de checkout
      // this.$router.push({ name: 'checkout' });
      
      this.$toast.info('Redirecionando para o checkout...');
    },
    
    navigateToProduct(product) {
      this.$router.push({
        name: 'shop.organization.product',
        params: {
          orgSlug: this.$route.params.orgSlug,
          productId: product.id
        }
      });
    }
  }
};
</script>

<style scoped>
.product-detail {
  padding-bottom: 60px;
}

.main-image {
  border: 1px solid #e0e0e0;
}

.thumbnail {
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.2s ease;
}

.thumbnail-active {
  border: 2px solid var(--v-primary-base);
}

.gap-3 {
  gap: 12px;
}

@media (max-width: 599px) {
  .product-info {
    margin-top: 20px;
  }
}
</style> 